import styled from '@emotion/styled';
import {Color, useThemeColors} from '@nfq/colors';
import {spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';

import {P} from 'UI/components/layout/Text';

import type {WithOnlyTextChild} from 'types/global';

/**
 * The `ComponentProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface ComponentProps {
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'TimeBanner'
     */
    testId?: string;
}

/**
 * The `TimeBanner` Component.
 *
 * @param props          The component props.
 * @param props.testId   A unique identifier, usually in the form of a string, assigned to the component for testing purposes.
 * @param props.children The children.
 * @returns A React element representing the `TimeBanner` component.
 * @todo Add true documentation!
 *
 * @example
 * ```tsx
 * const MyComponent = <TimeBanner testId="myTestId">MyComponent</TimeBanner>;
 * ```
 */
const TimeBanner = observer(({children, testId = 'TimeBanner'}: WithOnlyTextChild<ComponentProps>) => {
    const colors = useThemeColors();

    return (
        <Wrapper data-cy={testId}>
            <P $size="large">
                <Color $color={colors.bgPrimary}>
                    <strong>{children}</strong>
                </Color>
            </P>
        </Wrapper>
    );
});

TimeBanner.displayName = 'TimeBanner';

export {TimeBanner};

const Wrapper = styled.div`
    background: ${({theme}) => theme.colors.primary};
    border-radius: 70px;
    max-width: 100%;
    padding-block: ${spacing(2)};
    padding-inline: ${spacing(6)};
    width: max-content;
`;